import {
  ExpressCheckoutElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import {
  StripeExpressCheckoutElementOptions,
  StripeExpressCheckoutElementReadyEvent,
  StripeExpressCheckoutElementClickEvent,
  StripeExpressCheckoutElementShippingAddressChangeEvent,
  StripeExpressCheckoutElementConfirmEvent,
  ClickResolveDetails,
} from "@stripe/stripe-js";
import { captureException } from "@sentry/gatsby";
import { sha256, Message } from "js-sha256";
import { useState, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { navigate } from "../../services/navigation";
import cartProductSelectors from "../../store/cart-product/selectors";
import i18nSelectors from "../../store/i18n/selectors";
import metrics from "../../utils/metrics";
import {
  trackCheckoutStarted,
  trackOrderCompleted,
} from "../../utils/tracking/cart";
import {
  finalizePurchase,
  getApplePayOption,
  tryApiPurchase,
  tryCreateConfirmationToken,
  tryHandleNextAction,
  getShippingRates,
  getExpressCheckoutPaymentMethods,
  getExpressCheckoutLineItems,
  onExpressCheckoutShippingAddressChange,
  tryElementsSubmit,
  getStripeElementsAppearanceVariables,
  getPurchasedCart,
  getAppliedPromotion,
} from "../checkout/utils";
import { ExpressCheckoutVariationCohort } from "./types";
import { capitalize } from "../../utils/style";

import CartProduct from "../../store/cart-product/model";
import Cart from "../../store/cart/model";
import Coupon from "../../store/coupon/model";
import { clearStorage } from "./utils/storage";

const ExpressCheckoutContainer = styled.div`
  padding: 0;
  min-height: 48px;
  width: 100%;
`;

const Underline = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  margin-top: 32px;
  margin-bottom: 32px;
  justify-content: center;
  align-items: center;
  &:before,
  &:after {
    content: "";
    flex: 1 1;
    border-bottom: 1px solid var(--indigo-blue-20, #d0d5e2);
    margin: auto;
  }
`;

const Eyebrow = styled.span`
  text-transform: uppercase;
  margin-left: 8px;
  margin-right: 8px;
`;

export interface Props {
  cart: Cart;
  cartProducts: CartProduct[];
  setShowErrorBanner: React.Dispatch<React.SetStateAction<boolean>>;
  setExpressCheckoutProcessing: React.Dispatch<React.SetStateAction<boolean>>;
  supportedExpressCheckoutPaymentMethods: ExpressCheckoutVariationCohort;
  coupon?: Coupon;
  location?: "home_checkout" | "flyout cart";
  showPayAnotherWay?: boolean;
}

const StripeExpressCheckout = ({
  cart,
  cartProducts,
  coupon,
  setShowErrorBanner,
  setExpressCheckoutProcessing,
  supportedExpressCheckoutPaymentMethods,
  location = "home_checkout",
  showPayAnotherWay = true,
}: Props) => {
  const stripe = useStripe();
  const elements = useElements();
  const activeCartProducts = useSelector(
    cartProductSelectors.sortedActiveCartProducts,
  );
  const hasMultipleCadences =
    Object.keys(cart.recurringBillingDetails).length > 1;

  const [display, setDisplay] = useState("none");
  const activeStore = useSelector(i18nSelectors.activeStore);

  const expressCheckoutOptions: StripeExpressCheckoutElementOptions =
    useMemo(() => {
      const paymentMethods = getExpressCheckoutPaymentMethods(
        supportedExpressCheckoutPaymentMethods,
      );
      return {
        buttonHeight: 48,
        paymentMethodOrder: [
          "apple_pay",
          "google_pay",
          "amazon_pay",
          "paypal",
          "link",
        ],
        shippingAddressRequired: true,
        emailRequired: true,
        phoneNumberRequired: false,
        allowedShippingCountries: activeStore.countries,
        layout: { maxRows: 1, maxColumns: 1, overflow: "auto" },
        applePay: getApplePayOption(cart),
        paymentMethods,
      };
    }, [supportedExpressCheckoutPaymentMethods, activeStore.countries, cart]);

  useEffect(() => {
    if (elements) {
      elements.update({
        appearance: {
          variables: getStripeElementsAppearanceVariables({
            borderRadius: "25px",
          }),
        },
      });
    }
  }, [elements]);

  const displayError = () => {
    setShowErrorBanner(true);
    setExpressCheckoutProcessing(false);
  };

  const onReady = ({
    availablePaymentMethods,
  }: StripeExpressCheckoutElementReadyEvent) => {
    if (!availablePaymentMethods) return;
    if (!Object.values(availablePaymentMethods).some(Boolean)) return;

    setDisplay("block");
  };

  const onClick = ({
    resolve,
    expressPaymentType,
  }: StripeExpressCheckoutElementClickEvent) => {
    const paymentMethodReadable = capitalize(
      expressPaymentType.replace(/_/g, " "),
    );
    metrics.track("CTA Clicked", {
      title: `Express Checkout ${paymentMethodReadable}`,
      location,
    });

    trackCheckoutStarted(`Express Checkout ${paymentMethodReadable}`);

    const lineItems = getExpressCheckoutLineItems(cartProducts, cart);

    const options: ClickResolveDetails = {
      lineItems: lineItems,
      shippingRates: getShippingRates(expressPaymentType, cart.shippingAmount),
    };

    if (cart.recurringBillingDetails && expressPaymentType === "apple_pay") {
      options.applePay = getApplePayOption(cart);
    }

    sessionStorage.setItem("rit-purchased_cart", JSON.stringify(cart));
    sessionStorage.setItem(
      "rit-purchased_cart_products",
      JSON.stringify(cartProducts),
    );

    resolve(options);
  };

  const onCancel = () => {
    setShowErrorBanner(false);
    clearStorage();

    metrics.track("Payment Info Cancelled", {
      payment_element: "Express Checkout",
    });
  };

  const onShippingAddressChange = async ({
    resolve,
    address,
  }: StripeExpressCheckoutElementShippingAddressChangeEvent) => {
    const { updatedLineItems, amount, currency } =
      await onExpressCheckoutShippingAddressChange(
        cart,
        cartProducts,
        address,
        coupon,
      );

    elements!.update({
      amount,
      currency,
    });

    resolve({
      lineItems: updatedLineItems,
    });
  };

  const onConfirm = async (event: StripeExpressCheckoutElementConfirmEvent) => {
    if (!stripe || !elements) {
      // Stripe.js hasn't loaded yet.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    setExpressCheckoutProcessing(true);

    metrics.track("Payment Info Entered", {
      payment_method: event.expressPaymentType,
      payment_element: "Express Checkout",
    });

    const { error: submitError } = await tryElementsSubmit(elements);
    if (submitError) {
      displayError();

      return;
    }

    // Store the event and cart in session storage so we can access it on the confirmation page.
    sessionStorage.setItem(
      "rit-express_checkout_confirm_event",
      JSON.stringify({
        ...event,
        paymentMethod: { type: event.expressPaymentType },
      }),
    );

    const { error: confirmationTokenError, confirmationToken } =
      await tryCreateConfirmationToken(stripe, elements);
    if (confirmationTokenError || !confirmationToken) {
      displayError();

      return;
    }

    sessionStorage.setItem("rit-cart_id", cart.id);
    sessionStorage.setItem(
      "rit-purchased_cart_products",
      JSON.stringify(activeCartProducts),
    );

    const { response: purchase, error } = await tryApiPurchase({
      confirmationToken,
      cartId: cart.id,
    });
    if (error || !purchase) {
      displayError();

      return;
    }

    metrics.identify(purchase.user_id, {
      first_name: purchase.first_name,
      last_name: purchase.last_name,
      email: event.billingDetails?.email,
    });
    sessionStorage.setItem("rit-checkout", "home");
    sessionStorage.setItem("rit-order_number", purchase.order_number);

    if (purchase.reset_password_token) {
      sessionStorage.setItem(
        "rit-reset_password_token",
        purchase.reset_password_token,
      );

      metrics.track("User Signup", {
        method: "provider:ritual",
        userHashedEmail: sha256(event.billingDetails?.email as Message),
      });
    }

    if (purchase.requires_action && purchase.client_secret) {
      const { error: handleNextActionError } = await tryHandleNextAction(
        purchase.client_secret,
        stripe,
      );

      if (handleNextActionError) {
        displayError();

        return;
      }

      await finalizePurchase(cart.id);
    }

    try {
      trackOrderCompleted({
        orderNumber: purchase.order_number,
        cartProducts: activeCartProducts,
        paymentMethod: event.expressPaymentType,
        checkoutType: "express_checkout",
        extraOptions: {
          addMarketingPreference: true,
          marketingPreference: undefined,
        },
        cart: getPurchasedCart(),
        appliedPromotion: getAppliedPromotion(),
        location,
      });
    } catch (e) {
      captureException(e);
    }

    setExpressCheckoutProcessing(false);

    navigate("/confirmation");
  };

  if (hasMultipleCadences) return null;

  return (
    <div style={{ display }} id="express-checkout-parent-container">
      <ExpressCheckoutContainer id="express-checkout-element">
        <ExpressCheckoutElement
          options={expressCheckoutOptions}
          onReady={onReady}
          onClick={onClick}
          onConfirm={onConfirm}
          onShippingAddressChange={onShippingAddressChange}
          onCancel={onCancel}
          onEscape={onCancel}
        />
      </ExpressCheckoutContainer>

      {showPayAnotherWay && (
        <Underline>
          <Eyebrow className="typography-eyebrow2 font-circular">
            Or pay another way
          </Eyebrow>
        </Underline>
      )}
    </div>
  );
};

export default StripeExpressCheckout;
